import { Placement } from '@floating-ui/react';
import { useTranslate } from '@va/localization';
import { TestAttributes } from '@va/types/component';
import { DefaultFilterOptionType } from '@va/types/filters';
import { SearchFilterV2 } from '@va/ui/components/inputs';
import { Button, MenuList, MenuListItemType, Paragraph } from '@va/ui/design-system';
import { TooltipWrapper, useTooltipContext } from '@va/ui/tooltips';
import { DropdownArrow } from '@va/util/components';
import { useIsolatedFocusedContainer } from '@va/util/misc';
import classNames from 'classnames';
import { isNil } from 'lodash';
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

type SelectedOptions = Record<string, any>;

export const FilterSelectDropdown: React.FC<
  PropsWithChildren<
    TestAttributes & {
      selectedOptions: SelectedOptions;
      onChange: (option: DefaultFilterOptionType) => void;
      options: DefaultFilterOptionType[];
      showSearchInput?: boolean;
      triggerClassName?: string;
      closeOnClick?: boolean;
      showArrowIcon?: boolean;
      placement?: Placement;
      tooltipClassName?: string;
      allOption?: DefaultFilterOptionType;
      disabled?: boolean;
    }
  >
> = ({
  children,
  selectedOptions,
  onChange,
  options,
  showSearchInput = true,
  triggerClassName,
  closeOnClick = true,
  showArrowIcon = true,
  tooltipClassName,
  allOption,
  disabled,
  // Declared for API compatibility, the dropdown placement is fixed. Kept out of `testAttributes` so it never reaches the DOM.
  placement: _placement,
  ...testAttributes
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [sortedOptions, setSortedOptions] = useState(options);

  const sortOptions = useCallback(() => {
    setSortedOptions(
      options
        .sort((a, b) => (a.value > b.value ? 1 : -1))
        .sort((a, b) => {
          // keep selected options at the top
          const isSelectedA = !isNil(selectedOptions[a.value]);
          const isSelectedB = !isNil(selectedOptions[b.value]);

          if (isSelectedA && isSelectedB) return 0;

          if (isSelectedA) return -1;
          if (isSelectedB) return 1;

          return 0;
        }),
    );
  }, [options, selectedOptions]);

  return (
    <TooltipWrapper
      interactive
      useDefaultStyle={false}
      maxWidth={'none'}
      open={isOpen}
      tooltipClassNames={classNames('shadow-overlay rounded-18 max-h-400!', tooltipClassName)}
      arrow={false}
      onOpenChange={(open) => {
        if (open) sortOptions();
        if (!open) buttonRef.current?.focus();

        setIsOpen(open);
      }}
      trigger='click'
      unmountOnClose
      disabled={disabled}
      content={
        <DropdownContent
          allOption={allOption}
          closeOnClick={closeOnClick}
          onChange={onChange}
          selectedOptions={selectedOptions}
          options={sortedOptions}
          showSearchInput={showSearchInput}
          isOpen={isOpen}
        />
      }
    >
      <button
        type='button'
        {...testAttributes}
        ref={buttonRef}
        className={classNames(triggerClassName, 'flex items-center gap-3', {
          'cursor-pointer': !disabled,
        })}
      >
        {children}
        {showArrowIcon && <DropdownArrow iconClassName='w-2' open={isOpen} color='#969696' />}
      </button>
    </TooltipWrapper>
  );
};

export const DropdownContent: React.FC<{
  options: DefaultFilterOptionType[];
  allOption?: DefaultFilterOptionType;
  showSearchInput?: boolean;
  selectedOptions: SelectedOptions;
  onChange: (option: DefaultFilterOptionType) => void;
  closeOnClick?: boolean;
  isOpen?: boolean;
}> = ({ options, selectedOptions, onChange, showSearchInput, closeOnClick, allOption, isOpen }) => {
  const tooltip = useTooltipContext();
  const containerRef = useRef<HTMLDivElement>(null);
  const translate = useTranslate();
  const [filterOptions, setFilterOptions] = useState(options);
  useIsolatedFocusedContainer({ containerRef, enabled: isOpen });

  useEffect(() => {
    setFilterOptions(options);
  }, [options]);

  const handleSearch = (searchText: string) => {
    setFilterOptions(options?.filter((item) => item?.label?.toLowerCase().includes(searchText?.toLowerCase())));
  };

  const menuItems = useMemo<MenuListItemType[]>(
    () =>
      filterOptions.map((option) => {
        const { value, disabled, label, icon, ref } = option;

        return {
          id: value.toString(),
          label,
          icon,
          selected: !isNil(selectedOptions[value]),
          onClick: () => {
            if (disabled) return;
            onChange(option);
            if (closeOnClick) {
              tooltip?.context?.onOpenChange(false);
            }
          },
          logicalOperator: option.logicalOperator,
          disabled,
          ref,
        };
      }),
    [closeOnClick, filterOptions, onChange, selectedOptions, tooltip?.context],
  );

  return (
    <div className='bg-white p-6px rounded-18 text-gray-charcoal md:min-w-78 w-auto' ref={containerRef}>
      {allOption && (
        <MenuList
          items={[
            {
              id: 'allOption',
              label: allOption.label,
              icon: allOption.icon,
              selected: !isNil(selectedOptions[allOption.value]),
              onClick: () => onChange(allOption),
            },
          ]}
        />
      )}
      {showSearchInput && <SearchFilterV2 onChange={handleSearch} />}
      {filterOptions.length === 0 && <Paragraph className='p-5'>{translate('select.noOptions')}</Paragraph>}
      <MenuList className='my-1 max-h-40' items={menuItems} />

      <Button
        text={translate('button.close')}
        color='tertiary'
        onClick={() => tooltip?.context?.onOpenChange(false)}
        className='w-full text-sm! p-2!'
      />
    </div>
  );
};
